import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import "../../assets/styles/AdminShared.css";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

const ProposalCreation = () => {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState("");
  const [proposal, setProposal] = useState(null);
  const [keyDates, setKeyDates] = useState([]);
  const [stages, setStages] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [showPreview, setShowPreview] = useState(false);

  const titleFromKey = (value) =>
    value.replace(/([A-Z])/g, " $1").trim().replace(/^./, s => s.toUpperCase());

  const updateProposalField = (field, value) => {
    setProposal(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (field, index, value) => {
    setProposal(prev => ({
      ...prev,
      [field]: (prev[field] || []).map((item, i) => i === index ? value : item),
    }));
  };

  const addArrayItem = (field) => {
    setProposal(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), ""],
    }));
  };

  const removeArrayItem = (field, index) => {
    setProposal(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index),
    }));
  };

  const updateCarePlanField = (field, value) => {
    setProposal(prev => ({
      ...prev,
      carePlanOption: {
        title: "",
        price: "",
        description: "",
        ...(prev.carePlanOption || {}),
        [field]: value,
      },
    }));
  };

  const renderArrayEditor = (field, label) => (
    <div className="ap-form-group">
      <label>{label}</label>
      {(proposal[field] || []).map((item, index) => (
        <div key={index} className="ap-list-item" style={{ marginBottom: '8px', alignItems: 'flex-start' }}>
          <textarea
            value={item}
            onChange={e => updateArrayItem(field, index, e.target.value)}
            placeholder={`Add ${label.toLowerCase()} item`}
            style={{ minHeight: '64px' }}
          />
          <button className="ap-btn-danger" style={{ padding: '8px 12px', fontSize: '0.8125rem' }} onClick={() => removeArrayItem(field, index)}>
            Remove
          </button>
        </div>
      ))}
      <button className="ap-btn-secondary" style={{ fontSize: '0.8125rem', padding: '7px 14px' }} onClick={() => addArrayItem(field)}>
        + Add Item
      </button>
    </div>
  );

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const projectRef = doc(db, "projects", projectId);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const data = projectSnap.data();
          setProjectName(data.projectName || "Unnamed Project");
          setProposal(data.proposalDocuments.proposal || null);
          setKeyDates(data.keyDates || []);
          setStages(data.stages || []);
        }
      } catch (error) {
        console.error("Error fetching project details:", error);
      }
    };
    fetchProjectDetails();
  }, [projectId]);

  const handleAddServiceItem = (section, value) => {
    setProposal(prev => ({
      ...prev,
      serviceDetails: { ...prev.serviceDetails, [section]: [...prev.serviceDetails[section], value] },
    }));
  };

  const handleRemoveServiceItem = (section, index) => {
    setProposal(prev => ({
      ...prev,
      serviceDetails: { ...prev.serviceDetails, [section]: prev.serviceDetails[section].filter((_, i) => i !== index) },
    }));
  };

  const handleSaveProposal = async () => {
    try {
      await updateDoc(doc(db, "projects", projectId), {
        "proposalDocuments.proposal": proposal,
        keyDates,
        stages,
      });
      setMessage("Proposal updated successfully!");
      setMessageType("success");
    } catch (error) {
      console.error("Error saving proposal:", error);
      setMessage("Failed to update the proposal. Please try again.");
      setMessageType("error");
    }
  };

  const saveStagesToFirestore = async (updatedStages) => {
    try {
      await updateDoc(doc(db, "projects", projectId), { stages: updatedStages });
      setMessage("Timeline updated successfully!");
      setMessageType("success");
    } catch (error) {
      setMessage("Failed to update the timeline. Please try again.");
      setMessageType("error");
    }
  };

  const handleAddEvent = (eventInfo) => {
    const { start, end } = eventInfo;
    const eventName = prompt("Enter event name:");
    if (eventName) {
      const newEvent = {
        name: eventName,
        start: start.toISOString().split("T")[0],
        end: end.toISOString().split("T")[0],
      };
      const updated = [...stages, newEvent];
      setStages(updated);
      saveStagesToFirestore(updated);
    }
  };

  const handleEventDelete = (eventInfo) => {
    if (window.confirm(`Delete event "${eventInfo.event.title}"?`)) {
      const updated = stages.filter(s => s.name !== eventInfo.event.title);
      setStages(updated);
      saveStagesToFirestore(updated);
    }
  };

  const handleEventChange = (changeInfo) => {
    const { event } = changeInfo;
    setStages(prev => prev.map(stage =>
      stage.name === event.title
        ? { ...stage, start: event.start.toISOString().split("T")[0], end: event.end ? event.end.toISOString().split("T")[0] : event.start.toISOString().split("T")[0] }
        : stage
    ));
  };

  if (!proposal) return <div className="ap-loading"><p>Loading proposal...</p></div>;

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <div className="ap-page-header-row">
          <div>
            <p className="ap-eyebrow">Admin — Proposal</p>
            <h1 className="ap-title">{projectName}</h1>
          </div>
          <div className="ap-btn-row">
            <button className="ap-btn-secondary" onClick={() => setShowPreview(true)}>Preview</button>
            <button className="ap-btn-primary" onClick={handleSaveProposal}>Save Proposal</button>
          </div>
        </div>
        {message && <p className={messageType === "error" ? "ap-message--error" : "ap-message"} style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', marginTop: '12px' }}>{message}</p>}
      </div>

      {/* Proposal details */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Proposal Details</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-group">
            <label>Goals for This Project</label>
            <textarea value={proposal.goals || ""} onChange={e => updateProposalField("goals", e.target.value)} placeholder="Define the goals for this project" />
          </div>
          <div className="ap-form-group">
            <label>Project Summary</label>
            <textarea value={proposal.summary || ""} onChange={e => updateProposalField("summary", e.target.value)} placeholder="Short outcome-focused summary of what this client is buying" />
          </div>
          <div className="ap-form-group">
            <label>Service Name</label>
            <input type="text" value={proposal.service || ""} onChange={e => updateProposalField("service", e.target.value)} placeholder="Starter Website, Growth Website, Authority Website..." />
          </div>
          <div className="ap-form-group">
            <label>Investment</label>
            <input type="text" value={proposal.investment || ""} onChange={e => updateProposalField("investment", e.target.value)} placeholder="Enter the investment amount" />
          </div>
          <div className="ap-form-group">
            <label>Next Steps</label>
            <textarea value={proposal.nextSteps || ""} onChange={e => updateProposalField("nextSteps", e.target.value)} placeholder="Explain what the client should do after reviewing the proposal" />
          </div>
        </div>
      </div>

      {/* Payment and boundaries */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Payment and Boundaries</h2>
        </div>
        <div className="ap-section-body">
          {renderArrayEditor("notIncluded", "Not Included")}
          {renderArrayEditor("paymentSchedule", "Payment Schedule")}
        </div>
      </div>

      {/* Care plan */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Care Plan Recommendation</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-group">
            <label>Care Plan Title</label>
            <input type="text" value={proposal.carePlanOption?.title || ""} onChange={e => updateCarePlanField("title", e.target.value)} placeholder="Basic Care Plan" />
          </div>
          <div className="ap-form-group">
            <label>Care Plan Price</label>
            <input type="text" value={proposal.carePlanOption?.price || ""} onChange={e => updateCarePlanField("price", e.target.value)} placeholder="$149/mo" />
          </div>
          <div className="ap-form-group">
            <label>Care Plan Description</label>
            <textarea value={proposal.carePlanOption?.description || ""} onChange={e => updateCarePlanField("description", e.target.value)} placeholder="Why this care plan is recommended after launch" />
          </div>
        </div>
      </div>

      {/* Service details */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Service Details</h2>
        </div>
        <div className="ap-section-body">
          {Object.entries(proposal.serviceDetails || {}).map(([section, items]) => (
            <div key={section} style={{ marginBottom: '16px' }}>
              <p className="ap-eyebrow" style={{ marginBottom: '8px' }}>{titleFromKey(section)}</p>
              {items.map((item, index) => (
                <div key={index} className="ap-list-item" style={{ marginBottom: '8px' }}>
                  <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>{item}</span>
                  <button className="ap-btn-danger" style={{ padding: '6px 12px', fontSize: '0.8125rem' }} onClick={() => handleRemoveServiceItem(section, index)}>
                    Remove
                  </button>
                </div>
              ))}
              <button className="ap-btn-secondary" style={{ marginTop: '8px', fontSize: '0.8125rem', padding: '7px 14px' }} onClick={() => {
                const val = prompt(`Add item to ${section}:`);
                if (val) handleAddServiceItem(section, val);
              }}>
                + Add Item
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline calendar */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Timeline</h2>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Click a date range to add · Click event to delete</span>
        </div>
        <div style={{ padding: '24px' }}>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            editable={true}
            selectable={true}
            events={stages.map(stage => ({ title: stage.name, start: stage.start, end: stage.end }))}
            select={handleAddEvent}
            eventClick={handleEventDelete}
            eventChange={handleEventChange}
            eventResizableFromStart={true}
          />
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="ap-modal-overlay">
          <div className="ap-modal" style={{ maxWidth: '640px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h2>Proposal Preview</h2>
            <p style={{ marginBottom: '8px' }}><strong>Title:</strong> {proposal.title}</p>
            <p style={{ marginBottom: '8px' }}><strong>Service:</strong> {proposal.service}</p>
            <p style={{ marginBottom: '8px' }}><strong>Summary:</strong> {proposal.summary}</p>
            <p style={{ marginBottom: '8px' }}><strong>Goals:</strong> {proposal.goals}</p>
            <p style={{ marginBottom: '16px' }}><strong>Investment:</strong> {proposal.investment}</p>
            {["notIncluded", "paymentSchedule"].map(field => (
              <div key={field} style={{ marginBottom: '12px' }}>
                <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>{titleFromKey(field)}</p>
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  {(proposal[field] || []).map((item, i) => <li key={i} style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>{item}</li>)}
                </ul>
              </div>
            ))}
            {proposal.carePlanOption && (
              <p style={{ marginBottom: '16px' }}><strong>Care Plan:</strong> {proposal.carePlanOption.title} {proposal.carePlanOption.price} — {proposal.carePlanOption.description}</p>
            )}
            <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>Service Details</p>
            {Object.entries(proposal.serviceDetails || {}).map(([section, items]) => (
              <div key={section} style={{ marginBottom: '12px' }}>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', margin: '0 0 6px' }}>{section}</p>
                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                  {items.map((item, i) => <li key={i} style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>{item}</li>)}
                </ul>
              </div>
            ))}
            <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, color: 'var(--text-primary)', margin: '16px 0 8px' }}>Timeline</p>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {stages.map((stage, i) => (
                <li key={i} style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {stage.name}: {stage.start || "N/A"} – {stage.end || "N/A"}
                </li>
              ))}
            </ul>
            <div className="ap-btn-row" style={{ marginTop: '24px' }}>
              <button className="ap-btn-secondary" onClick={() => setShowPreview(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalCreation;
